import axios from 'axios';
import moment from 'moment-timezone';

import { resolveVoieCode, splitRoute, UNKNOWN_VOIE_CODE } from './addressTypes';
import { makeAuthenticatedApiCall } from './apiHelper';
import { fuelOptions } from './constants/cotation';
import { DBDeal } from './types/deal';

export const Capitalize = (str: string) => {
  if (str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
};

export const capitalizeFirstLetter = (str: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const capitalizeAndFormat = (str: string) => {
  if (!str) return str;

  // Diviser la chaîne en mots en utilisant l'underscore comme séparateur
  const words = str.split('_');

  // Capitaliser la première lettre de chaque mot
  const capitalizedWords = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

  // Joindre les mots avec un espace et retourner le résultat
  return capitalizedWords.join(' ');
};

// Create our number formatter.
export const priceFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',

  // These options are needed to round to whole numbers if that's what you want.
  //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
  maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
});

export const numberFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'decimal',
  maximumFractionDigits: 0,
});

export const formatKilometers = (kilometers: number): string => {
  if (kilometers) {
    const formattedKilometers = kilometers?.toLocaleString('fr-FR', {
      maximumFractionDigits: 0, // Pas de décimales
    });

    // Ajoute "km" à la fin du résultat
    return `${formattedKilometers} km`;
  }
  return '';
};

export const formatString = (str: string) => {
  if (str === '') return '';

  // 1. Divisez la chaîne de caractères par `:`.
  const [initialKey, initialValue] = str.split(':').map((s) => s.trim());

  // 2. Capitalisez la première lettre de chaque mot du premier segment.
  const key = initialKey
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  // 3. Capitalisez la première lettre de chaque mot du second segment après `-`.
  const value = initialValue.includes('-')
    ? initialValue
        .split('-')
        .map((s) => s.trim())
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' - ')
    : initialValue.charAt(0).toUpperCase() + initialValue.slice(1);

  // 4. Remettez les deux segments ensemble en les joignant avec `:`.
  return `${key} : ${value}`;
};

export const formatDamages = (damagesString: string): string[] => {
  if (!damagesString || typeof damagesString !== 'string') {
    return [];
  }

  let workingString = damagesString.trim();

  // Essayer de parser en tant que JSON (possiblement stringifié plusieurs fois)
  let maxAttempts = 3; // Limite pour éviter une boucle infinie
  let parsed: any = workingString;

  for (let i = 0; i < maxAttempts; i++) {
    try {
      parsed = JSON.parse(parsed);
      // Si on obtient un tableau, on a terminé
      if (Array.isArray(parsed)) {
        return parsed.map((damage) => formatString(damage)).filter((damage) => damage.trim() !== '');
      }
      // Si c'est toujours une chaîne, continuer à parser
      if (typeof parsed !== 'string') {
        break;
      }
    } catch (error) {
      // Le parse a échoué, sortir de la boucle
      break;
    }
  }

  // Si on arrive ici, le JSON parse n'a pas fonctionné
  // Essayer le format avec \n
  let cleanedString = workingString;

  // Enlever les guillemets au début et à la fin (peut-être plusieurs paires)
  while (cleanedString.startsWith('"') && cleanedString.endsWith('"') && cleanedString.length > 2) {
    cleanedString = cleanedString.slice(1, -1);
  }

  // Vérifier si c'est du JSON après nettoyage
  try {
    const parsed = JSON.parse(cleanedString);
    if (Array.isArray(parsed)) {
      return parsed.map((damage) => formatString(damage)).filter((damage) => damage.trim() !== '');
    }
  } catch (e) {
    // Pas du JSON, continuer avec le format \n
  }

  // Format avec \n (ancien format)
  if (cleanedString.includes('\\n')) {
    const damagesArray = cleanedString.split(/\\n/).map((damage) => damage.replace(/\\+$/, '').trim());
    return damagesArray.map((damage) => formatString(damage)).filter((damage) => damage.trim() !== '');
  }

  // Si rien ne fonctionne, retourner la chaîne formatée telle quelle
  return [formatString(cleanedString)].filter((damage) => damage.trim() !== '');
};

/**
 * Parse récursivement les dommages (gère double/triple stringification JSON)
 */
const parseDamagesRecursively = (data: unknown, depth = 0): unknown => {
  if (depth >= 4 || typeof data !== 'string') return data;
  try {
    return parseDamagesRecursively(JSON.parse(data), depth + 1);
  } catch {
    return data;
  }
};

/** Filtre les chaînes vides ou blanches d'un tableau de dommages */
const filterNonEmptyDamages = (arr: string[]): string[] =>
  arr.filter((s) => typeof s === 'string' && s.trim() !== '');

/**
 * Convertit les dommages (string ou array) en tableau de strings valide.
 * Gère : undefined, null, JSON string, array, array avec éléments "", double stringification.
 */
const normalizeToDamagesArray = (damages: string | string[] | null | undefined): string[] | null => {
  if (damages == null) return null;

  if (Array.isArray(damages)) {
    const flatArray = damages.flat().filter((item): item is string => typeof item === 'string');
    const nonEmpty = filterNonEmptyDamages(flatArray);
    return nonEmpty.length > 0 ? nonEmpty : null;
  }

  if (typeof damages !== 'string') return null;

  try {
    const workingString = damages.includes('][') ? damages.replace('][', ',') : damages;
    const parsed = parseDamagesRecursively(workingString) ?? workingString;

    if (Array.isArray(parsed)) {
      const strings = parsed
        .filter((item): item is string => typeof item === 'string')
        .filter((s) => s.trim() !== '');
      return strings.length > 0 ? strings : null;
    }

    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      const values = Object.values(parsed)
        .filter((item): item is string => typeof item === 'string')
        .filter((s) => s.trim() !== '');
      return values.length > 0 ? values : null;
    }

    if (typeof parsed === 'string' && parsed.trim()) {
      return [parsed];
    }

    return null;
  } catch (e) {
    console.error('Error parsing damages:', e);
    return null;
  }
};

export const formatDamagesNoZero = (damages: string | string[] | null | undefined) => {
  const damagesArray = normalizeToDamagesArray(damages);

  if (!damagesArray || damagesArray.length === 0) {
    return 'Aucun dommage déclaré';
  }

  // Filtrer uniquement les dommages significatifs
  const significantDamages = damagesArray.filter((damage: string) => {
    const isSignificant =
      !damage.includes('aucun dommage') &&
      !damage.includes('etat conforme') &&
      !damage.includes('non grêlé') &&
      damage.includes('€') &&
      !damage.includes('(0 €)');
    return isSignificant;
  });

  if (significantDamages.length === 0) return 'Aucun dommage significatif';

  /** Extrait le montant (XXX €) à la fin d'une chaîne type "tôlerie + peinture (enfoncement) (721 €)" */
  const extractPriceFromDamage = (descriptionWithCost: string): number => {
    const match = descriptionWithCost.match(/\(([\d\s]+)\s*€\)\s*$/);
    if (!match) return 0;
    const priceStr = match[1].replace(/\s/g, '');
    return parseInt(priceStr, 10) || 0;
  };

  /** Retire le tarif final pour obtenir la description seule */
  const removePriceFromDescription = (descriptionWithCost: string): string =>
    descriptionWithCost.replace(/\s*\([\d\s]+\s*€\)\s*$/, '').trim();

  // Grouper les dommages similaires
  const groupedDamages = significantDamages.reduce(
    (
      acc: {
        [key: string]: {
          count: number;
          positions: string[];
          description: string;
          totalPrice: number;
        };
      },
      damage: string,
    ) => {
      const [part, descriptionWithCost] = damage.split(' : ');
      const basePart = part.split('_')[0];
      const position = part.split('_')[1]?.toUpperCase();
      const description = removePriceFromDescription(descriptionWithCost);
      const price = extractPriceFromDamage(descriptionWithCost);

      if (!acc[basePart]) {
        acc[basePart] = {
          count: 1,
          positions: position ? [position] : [],
          description,
          totalPrice: price,
        };
      } else {
        acc[basePart].count++;
        acc[basePart].totalPrice += price;
        if (position) acc[basePart].positions.push(position);
      }
      return acc;
    },
    {},
  );

  // Formater en texte naturel avec des puces et les tarifs
  const formattedDamages = Object.entries(groupedDamages)
    .map(([part, info]) => {
      const count = info.count > 1 ? `${info.count} ` : '';
      const article = count ? '' : 'Un(e) ';
      const partName = part.replace(/_/g, ' ');
      const positions = info.positions.length > 0 ? ` (${info.positions.join(', ')})` : '';
      const priceFormatted = info.totalPrice > 0 ? ` - ${priceFormatter.format(info.totalPrice)}` : '';

      return `<br/>• ${article}${count}${partName}${positions} ${info.description}${priceFormatted}`;
    })
    .join('');

  // Retourner le texte formaté avec un div englobant pour le HTML
  return `<div style="margin-left: 20px; color: black;">${formattedDamages.replace(/^<br\/>/, '')}</div>`;
};

export const convertToISO = (dateTimeStr: string) => {
  // Séparation de la date et de l'heure
  const [date, time] = dateTimeStr.split(' ');

  // Séparation des composants de la date
  const [day, month, year] = date.split('/');

  // Concaténation pour former la nouvelle chaîne de date-heure
  const newDateTime = `${year}-${month}-${day}T${time}`;

  // Création d'un objet Date pour s'assurer que le format est correct
  const dateObj = new Date(newDateTime);

  // Retourner la date en format ISO 8601
  return dateObj.toISOString().slice(0, 19);
};
export const formatDateToDDMMYYYY = (dateString: string) => {
  if (dateString) {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Les mois sont indexés de 0-11, donc on ajoute 1
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } else return '';
};

export const formatDateToYYYYMMDD = (dateString: string) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Les mois sont indexés de 0-11, donc on ajoute 1
  const year = date.getFullYear();

  return `${year}-${month}-${day}`;
};

// L'entrée est une date UTC ('2026-07-22 15:03:32', '2026-07-22T15:03:32' ou ISO avec Z :
// add_time Pipedrive, timestamps DB sérialisés par l'API).
export const formatDateToDDMMYYYYWithTime = (utcDateString: string) => {
  return moment.utc(utcDateString).tz('Europe/Paris').format('DD/MM/YYYY HH:mm');
};

export const formatSimpleDateToDDMMYYYYWithTime = (utcDateString: string) => {
  const isoDateString = utcDateString.replace(' ', 'T');

  // Créer un objet Date à partir de la chaîne de date ISO
  const date = new Date(isoDateString);

  const options = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Paris',
  } as Intl.DateTimeFormatOptions;

  // Formater la date en français
  return date.toLocaleString('fr-FR', options);
};

export const getFuelValueFromLabel = (label: string): string => {
  if (!label) return '';

  // Normaliser le label en enlevant les accents et en mettant en minuscules
  const normalizeString = (str: string) => {
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  };

  const normalizedLabel = normalizeString(label);
  const option = fuelOptions.find(
    (option) => normalizeString(option.label) === normalizedLabel || normalizeString(option.value) === normalizedLabel,
  );

  return option ? option.value : '';
};

// Fonction pour déterminer si l'URL est un PDF
export const isPdf = (file: File | string | null | undefined) => {
  // Vérifiez si 'file' est une chaîne de caractères
  if (typeof file === 'string') {
    return file.includes('.pdf');
  }
  // Vérifiez si 'file' est un objet 'File' et non null/undefined avant d'accéder à 'type'
  return file?.type === 'application/pdf';
};

export const extractFileName = (url: string) => {
  return decodeURIComponent(url?.split('/')?.pop()?.split('?')[0] ?? '');
};

export const formatPriceWithoutSpaces = (priceStr: string): string => {
  // Retirer les espaces et convertir en nombre
  const price = parseFloat(priceStr.replace(/\s+/g, ''));

  // Vérifier si le nombre est valide
  if (isNaN(price)) {
    return '0 €'; // ou toute autre valeur par défaut en cas d'erreur
  }

  // Formater le nombre en format monétaire
  return priceFormatter.format(price);
};

// Fonction pour convertir une chaîne base64 en Blob
export function base64ToBlob(base64: string, mimeType: string) {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}

// Fonction pour formater la plaque d'immatriculation
export const formatRegPlate = (regPlate: string) => {
  // Vérifie si regPlate est null ou undefined
  if (!regPlate) {
    return null; // ou retourner regPlate pour garder la valeur originale
  }
  const cleaned = regPlate.replace(/[-\s]/g, '').toUpperCase();
  const formatted = cleaned.replace(/^(.{2})(.{3})(.{2})$/, '$1-$2-$3');
  return isValidRegPlateFormat(formatted) ? formatted : regPlate;
};

const isValidRegPlateFormat = (regPlate: string) => /^[A-Z]{2}-\d{3}-[A-Z]{2}$/.test(regPlate);

export const getPipedriveDealData = async (dealId: number) => {
  const response = await axios.get(`/api/pipedrive/deals/${dealId}`);
  return response.data;
};

export const getPipedrivePersonData = async (personId: number) => {
  // Use local DB endpoint with Pipedrive fallback (handled in backend)
  const response = await axios.get(`/api/pipedrive/persons-in-db/${personId}`);
  return response.data;
};

export const getPipedriveOrgData = async (orgId: number) => {
  // Use local DB endpoint with Pipedrive fallback (handled in backend)
  const response = await axios.get(`/api/pipedrive/organizations-in-db/${orgId}`);
  return response.data;
};

// Types de voie : SOURCE DE VÉRITÉ UNIQUE = ./addressTypes.ts (registre + alias).
// On ré-exporte ADDRESS_TYPE_OPTIONS ici pour compat avec les imports existants
// (`import { ADDRESS_TYPE_OPTIONS } from '../utils'`).
export { ADDRESS_TYPE_OPTIONS, deriveAddressFromStructuredOrg, hasStructuredAddress, resolveVoieCode, splitRoute } from './addressTypes';

// Convertit un libellé/forme de type de voie en code du dropdown ('' si inconnu).
// Rétro-compat : délègue au registre (insensible casse/accents, alias legacy).
export const convertAddressTypeToCode = (typeVoie: string): string => {
  if (!typeVoie) return '';
  const code = resolveVoieCode(typeVoie);
  return code === UNKNOWN_VOIE_CODE ? '' : code;
};

// Sépare une route ("Avenue Ambroise Croizat") en { typeVoie: code, nomVoie }.
// Rétro-compat : délègue à splitRoute du registre. Historiquement SENSIBLE À LA
// CASSE et limité à 9 types (d'où « type = Inconnu » sur une route minuscule ou
// un type non listé — mail « Bug adresse DA ») : désormais insensible casse/accents
// et couvre toute la liste + alias.
export const getAdresseTypeVoie = (route: string): { typeVoie: string; nomVoie: string } => {
  const { typeCode, name } = splitRoute(route);
  return { typeVoie: typeCode, nomVoie: name };
};

export const documentTypes: Record<string, { label: string; dbValue: string }> = {
  '64': { label: 'CI', dbValue: 'id_card' },
  '65': { label: 'Passeport', dbValue: 'passport' },
  '66': { label: 'Permis de conduire', dbValue: 'driving_licence' },
  '70': { label: 'Titre de séjour', dbValue: 'residence_permit' },
};

export const documentTypesDb: Record<string, string> = {
  CI: 'id_card',
  Passeport: 'passport',
  'Permis de conduire': 'driving_licence',
  'Titre de séjour': 'residence_permit',
};

export const genderTypes: Record<string, { label: string; value: string }> = {
  '44': { label: 'Masculin', value: 'male' },
  '45': { label: 'Féminin', value: 'female' },
};

export const formatPriceToNumber = (priceStr: any): number => {
  // S'assurer que priceStr est une chaîne
  const priceString = String(priceStr);

  // Retirer les espaces et convertir en nombre
  const price = parseFloat(priceString.replace(/\s+/g, '').replace('€', ''));

  // Vérifier si le nombre est valide
  if (isNaN(price)) {
    return 0; // ou toute autre valeur par défaut en cas d'erreur
  }

  // Retourner le nombre sans formatage monétaire
  return price;
};

export function getCleanPrice(data: any) {
  const { final_purchase_price, purchase_price } = data;

  // Fonction pour nettoyer et convertir le prix en nombre
  const cleanAndParsePrice = (price: string) => {
    return parseInt(price.replace(/[^\d]/g, ''), 10);
  };

  // Supprime tous les types d'espaces blancs et caractères non-numériques
  const cleanedFinalPurchasePrice = final_purchase_price.replace(/\s/g, '').replace('€', '');

  // Vérification si final_purchase_price est "0" après le nettoyage
  if (cleanedFinalPurchasePrice === '0') {
    return cleanAndParsePrice(purchase_price);
  } else {
    return cleanAndParsePrice(final_purchase_price);
  }
}

// Fonction pour formater la date actuelle en DD/MM/YYYY
export function getFormattedCurrentDate() {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0'); // Les mois sont indexés à partir de 0
  const year = today.getFullYear();
  return `${day}/${month}/${year}`;
}

// Fonction pour comparer deux dates et vérifier si la seconde est plus vieille de 9 ans ou plus
export function isDateMoreThan9YearsOld(dateToCompare: string) {
  const currentDate = new Date();
  const compareDate = createDateFromString(dateToCompare);

  const yearsDifference = currentDate.getFullYear() - compareDate.getFullYear();
  const monthsDifference = currentDate.getMonth() - compareDate.getMonth();
  const daysDifference = currentDate.getDate() - compareDate.getDate();

  // Si l'écart est de 9 ans mais que le mois/jour courant n'est pas encore passé, soustraire 1 de l'écart d'années
  if (
    yearsDifference > 9 ||
    (yearsDifference === 9 && (monthsDifference > 0 || (monthsDifference === 0 && daysDifference >= 0)))
  ) {
    return true;
  } else {
    return false;
  }
}

// Convertit une chaîne DD/MM/YYYY en un objet Date
export function createDateFromString(dateString: string) {
  const [day, month, year] = dateString.split('/').map((num) => parseInt(num, 10));
  return new Date(year, month - 1, day); // Les mois en JavaScript commencent à 0
}

export function getFormatFromMimetype(mimetype: string): string {
  // Return the format based on the mimetype
  switch (mimetype) {
    case 'image/jpeg':
    case 'image/jpg':
      return 'jpg';
    case 'image/png':
      return 'png';
    case 'image/heic':
      return 'heic';
    case 'application/pdf':
      return 'pdf';
    case 'image/gif':
      return 'gif';
    case 'image/bmp':
      return 'bmp';
    case 'image/tiff':
      return 'tiff';
    case 'text/plain':
      return 'txt';
    case 'application/zip':
      return 'zip';
    // Add more cases for other mime types as needed
    default:
      return 'bin'; // Return 'bin' as a fallback for unknown mime types
  }
}

export function debounce<T extends (...args: any[]) => void>(func: T, wait: number): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function (this: ThisParameterType<T>, ...args: Parameters<T>): void {
    const context = this;
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

export const formatUTCDateTime = (utcDateString: string | null | undefined) => {
  if (!utcDateString) return '';
  return utcDateString.slice(0, 16);
};

export const removeSpaces = (value: string) => value.replace(/\s/g, '');

// Fonction pour valider un SIRET
export const validateSiret = (siret: string): string | true => {
  if (!siret || siret.trim() === '') {
    return true; // Pas d'erreur si le champ est vide
  }

  const cleanedSiret = siret.replace(/\D/g, '');
  const isSiren = cleanedSiret.length === 9;

  if (isSiren) {
    return 'Vous avez saisi un SIREN (9 chiffres). Un SIRET (14 chiffres) est requis.';
  }

  if (cleanedSiret.length !== 14) {
    return 'Le SIRET doit contenir exactement 14 chiffres';
  }

  if (!/^\d{14}$/.test(cleanedSiret)) {
    return 'Le SIRET doit contenir uniquement des chiffres';
  }

  return true;
};

// Fonction pour formater un SIRET avec espaces
export const formatSiret = (value: string): string => {
  const cleanValue = value.replace(/\D/g, '');

  let formattedValue = cleanValue;
  if (cleanValue.length > 3) {
    formattedValue = cleanValue.slice(0, 3) + ' ' + cleanValue.slice(3);
  }
  if (cleanValue.length > 6) {
    formattedValue = cleanValue.slice(0, 3) + ' ' + cleanValue.slice(3, 6) + ' ' + cleanValue.slice(6);
  }
  if (cleanValue.length > 9) {
    formattedValue =
      cleanValue.slice(0, 3) + ' ' + cleanValue.slice(3, 6) + ' ' + cleanValue.slice(6, 9) + ' ' + cleanValue.slice(9);
  }

  return formattedValue;
};

// Fonction pour nettoyer un SIRET (enlever les espaces et caractères non numériques)
export const cleanSiret = (value: string): string => {
  return value.replace(/\D/g, '');
};

export const updateDealStage = async (dealId: number, newStageId: number) => {
  try {
    await makeAuthenticatedApiCall('post', '/api/pipedrive/deals/update-stage-deal', {
      dealId: dealId,
      stageId: newStageId,
    });
    // refetch(); // Refresh the CG list after updates : Est-ce vraiment nécessaire ?
  } catch (error) {
    console.error('Error updating deal stage:', error);
  }
};

export const trimFormData = (formData: any) => {
  const trimmedData: any = {};
  for (const key in formData) {
    if (typeof formData[key] === 'string') {
      trimmedData[key] = formData[key].trim();
    } else {
      trimmedData[key] = formData[key];
    }
  }
  return trimmedData;
};

export const AgencyDealsListTableTypes = {
  Proposals: ['39', '14'], // A0-A1
  Buying: ['30', '32', '24', '33', '15'], // A2->A6
  Mandates: ['6', '1', '40', '43', '34', '35'], // B1->B6 (Hors LPN)
  DepositSale: ['36', '41', '42', '37', '44', '38'], // E1->E6 (Hors LPN)
  Done: ['9', '10', '45', '11', '35', '38'], // C + B6LPN + E6LPN
};

/**
 * Fonction pour transformer un row de database en row pipedrive
 * @param dbRow - Le row de la base de données
 * @returns Row formaté pour Pipedrive
 */
export const transformDBRowToPipedriveRow = (dbRow: any, t: any) => {
  const fuelTranslation = t(`fuel.${dbRow.fuel}`, dbRow.fuel);
  const transmissionTranslation = t(`transmission.${dbRow.transmission}`, dbRow.transmission);
  const colourTranslation = t(`colour.${dbRow.colour}`, dbRow.colour);

  return {
    id: dbRow.pipedrive_deal_id || dbRow.id, // Utilise l'ID Pipedrive s'il existe
    add_time: formatDateToDDMMYYYYWithTime(dbRow.add_time),
    org_name: dbRow.org_name || '',
    update_time: dbRow.updated_at ? formatDateToDDMMYYYYWithTime(dbRow.updated_at) : '',
    stage_id: dbRow.stage_id,
    pipeline_id: null, // À ajuster si vous avez un mapping pipeline
    title: `${dbRow.make?.toUpperCase()} ${dbRow.model} ${dbRow.derivative || ''} | ${dbRow.registration_plate}`.trim(),
    active: true, // Exemple, peut être ajusté
    status: 'lost', // Peut être dynamique si la BDD contient un champ status
    org_id: dbRow.org_id || null,
    person_id: dbRow.person_id || null,
    model: `${dbRow.model} ${dbRow.derivative || ''}`.trim(),
    date_of_circulation: formatDateToDDMMYYYY(dbRow.reg_date),
    fuel: fuelTranslation,
    brand: dbRow.make ? dbRow.make.toUpperCase() : '',
    mileage: formatKilometers(dbRow.kms),
    final_purchase_price: dbRow.final_purchase_price
      ? priceFormatter.format(Number(dbRow.final_purchase_price))
      : '0 €',
    encheres_vo_price: dbRow.encheres_vo_price ? priceFormatter.format(Number(dbRow.encheres_vo_price)) : '',
    offer_type: t(`business_type.${dbRow.business_type}`, dbRow.business_type),
    purchase_price: dbRow.purchase_price ? priceFormatter.format(Number(dbRow.purchase_price)) : '',
    deposit_sale_arbitrator: dbRow.deposit_sale_arbitrator || null,
    transmission: transmissionTranslation,
    registration: dbRow.registration_plate || '',
    options: dbRow.comp_infos_1 || dbRow.comp_infos_2 || '',
    color: colourTranslation,
    data_store: dbRow.spark_fds_encrypt || '',
    vin: dbRow.vin || '',
  };
};

export const getCorrectedExtUrl = (deal: DBDeal) => {
  if (!deal.ext_url) {
    return '';
  }
  const match = deal.ext_url.match(/\/car-deal\/(\d+)\//);
  if (match && match[1]) {
    const extUrlId = parseInt(match[1], 10);
    if (extUrlId !== deal.pipedrive_deal_id) {
      return deal.ext_url.replace(`/car-deal/${extUrlId}/`, `/car-deal/${deal.pipedrive_deal_id ?? deal.id}/`);
    }
  }
  return deal.ext_url;
};

export const getFranchise = (id: string | undefined): string => {
  if (!id) return 'Unknown';
  switch (id) {
    case '37':
      return 'Skipcar'; // Mais en vrai c'est Ewigo
    case '38':
      return 'Bye Buy Car';
    case '39':
      return 'Vell';
    case '40':
      return 'AutoEasy';
    case '41':
      return 'Autres';
    case '52':
      return 'Vago Auto';
    case '53':
      return 'Agence Automobiliere';
    case '56':
      return 'Skipcar Indépendants';
    case '57':
      return 'Skipcar Click and buy auto';
    case '59':
      return 'Biwiz';
    case '80':
      return 'Weecars';
    case '82':
      return 'Simplicicar';
    case '83':
      return 'Simplicicar';
    case '84':
      return 'Via Automobile';
    case '85':
      return 'BH Cars';
    default:
      return 'Unknown';
  }
};

/**
 * Retourne tous les IDs de franchises disponibles
 */
export const getAllFranchiseIds = (): string[] => {
  return ['37', '38', '39', '40', '41', '52', '53', '56', '57', '59', '80', '82', '83', '84', '85'];
};

/**
 * Retourne toutes les franchises avec leur ID et nom
 */
export const getAllFranchises = (): Array<{ id: string; name: string }> => {
  const ids = getAllFranchiseIds();
  return ids.map((id) => ({ id, name: getFranchise(id) }));
};

const DA_VAT_RATE = 0.2;
const DA_FEE_FALLBACK_HT = 10;

export const getDaFeeTtc = (daFeeHt: number | string | null | undefined): number => {
  const parsed = typeof daFeeHt === 'number' ? daFeeHt : parseFloat(daFeeHt ?? '');
  const ht = Number.isFinite(parsed) && parsed >= 0 ? parsed : DA_FEE_FALLBACK_HT;
  return ht * (1 + DA_VAT_RATE);
};

export const formatDaFeeTtc = (daFeeHt: number | string | null | undefined): string => {
  return `${getDaFeeTtc(daFeeHt).toFixed(2).replace('.', ',')} € TTC`;
};
